import React, {useMemo, useState} from 'react';
import {CButton, CInput} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {normalizarBusca} from "./executionResults";

export interface ItemSelecionado {
    id: number | string,
    name: string,
    codigo?: string | null,
    grupo?: string | null
}

interface Props {
    droppableId: string,
    itens: Array<ItemSelecionado>,
    vazio: string,
    onRemover: (id: number | string) => void,
    onRemoverTodos: () => void,
    /** Sem ele a lista não é reordenável */
    onReordenar?: (origem: number, destino: number) => void
}

/**
 * Lista do que já está no plano. A ordem é a da execução sequencial, por isso dá para arrastar
 * (o filtro desliga o arraste: reordenar uma lista filtrada confunde a posição real).
 */
const SelectedItemsList = ({droppableId, itens, vazio, onRemover, onRemoverTodos, onReordenar}: Props) => {
    const [filtro, setFiltro] = useState<string>('');
    const [confirmandoLimpar, setConfirmandoLimpar] = useState<boolean>(false);

    const termo = normalizarBusca(filtro);
    const visiveis = useMemo(() => itens
        .map((item, index) => ({item, index}))
        .filter(({item}) => !termo || normalizarBusca([item.codigo, item.name, item.grupo].filter(Boolean).join(' ')).includes(termo)),
        [itens, termo]);
    const podeArrastar = !!onReordenar && !termo;

    const aoSoltar = (resultado: DropResult) => {
        if (!resultado.destination || !onReordenar) {
            return;
        }
        onReordenar(resultado.source.index, resultado.destination.index);
    };

    if (itens.length === 0) {
        return <div className="tester-vazio py-4">{vazio}</div>;
    }

    return (
        <div className="tester-selecionados">
            <div className="d-flex flex-wrap align-items-center mb-2" style={{gap: '.5rem'}}>
                {itens.length > 5 && (
                    <CInput
                        type="search"
                        value={filtro}
                        onChange={(e: any) => setFiltro(e.target.value)}
                        placeholder={'Filtrar os ' + itens.length + ' selecionados'}
                        style={{flex: '1 1 200px'}}
                        size="sm"
                    />
                )}
                <div className="tester-flex-espaco"/>
                {confirmandoLimpar ? (
                    <span className="d-inline-flex align-items-center" style={{gap: '.35rem'}}>
                        <span className="tester-ajuda">Remover os {itens.length}?</span>
                        <CButton size="sm" color="danger" onClick={() => { onRemoverTodos(); setConfirmandoLimpar(false); }}>Remover</CButton>
                        <CButton size="sm" color="secondary" variant="outline" onClick={() => setConfirmandoLimpar(false)}>Voltar</CButton>
                    </span>
                ) : (
                    <CButton size="sm" color="danger" variant="ghost" onClick={() => setConfirmandoLimpar(true)}>
                        <CIcon name="cil-trash" className="mr-1"/>Remover todos
                    </CButton>
                )}
            </div>

            <DragDropContext onDragEnd={aoSoltar}>
                <Droppable droppableId={droppableId} isDropDisabled={!podeArrastar}>
                    {(provided) => (
                        <div className="tester-lista-selecionados" ref={provided.innerRef} {...provided.droppableProps}>
                            {visiveis.map(({item, index}, posicao) => (
                                // O índice do arraste é a posição na lista exibida; o número mostrado é a ordem real no plano
                                <Draggable key={String(item.id)} draggableId={droppableId + '-' + item.id} index={posicao} isDragDisabled={!podeArrastar}>
                                    {(dragProvided, snapshot) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={'tester-item-selecionado' + (snapshot.isDragging ? ' arrastando' : '')}
                                        >
                                            {podeArrastar && (
                                                <span className="alca" {...dragProvided.dragHandleProps} title="Arraste para mudar a ordem de execução">
                                                    <CIcon name="cil-move"/>
                                                </span>
                                            )}
                                            <span className="ordem">{index + 1}.</span>
                                            <span className="conteudo">
                                                <span className="nome" title={item.name}>
                                                    {item.codigo && <span className="tester-codigo mr-2">{item.codigo}</span>}
                                                    {item.name}
                                                </span>
                                                {item.grupo && <span className="grupo">{item.grupo}</span>}
                                            </span>
                                            <CButton size="sm" color="danger" variant="ghost" title="Remover do plano"
                                                     onClick={() => onRemover(item.id)} aria-label={'Remover ' + item.name}>
                                                <CIcon name="cil-x"/>
                                            </CButton>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                            {visiveis.length === 0 && <div className="p-3 tester-ajuda">Nenhum item com esse texto.</div>}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

export default SelectedItemsList;
